import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type { CampaignListItem, CreatedCampaign, DiscoverySource, Paginated } from "./types";

// Tenant is part of the key so a tenant switch can't show another tenant's cache
// (the app also reloads on switch, but this keeps the cache correct regardless).
const campaignsKey = () => ["campaigns", getActiveTenantId()] as const;

// GET /api/campaigns is paginated ({ items, total, page, pageSize }, cap 100). The
// Campaigns table shows all campaigns of a tenant (a handful in the MVP), so we ask for
// the max page and hand callers the plain array they already expect. Revisit with real
// page controls if a tenant ever has >100 campaigns.
const LIST_PAGE_SIZE = 100;

/** GET /api/campaigns — the Campaigns table. Returns the rows (unwrapped from the page). */
export function useCampaignsList() {
  return useQuery({
    queryKey: [...campaignsKey(), "list"],
    queryFn: async () =>
      (await httpClient<Paginated<CampaignListItem>>(`/api/campaigns?pageSize=${LIST_PAGE_SIZE}`)).items,
  });
}

/**
 * POST /api/campaigns — create a campaign, then refresh the list.
 * ADR-0023: `brief` (the market description) + the chosen `source` are persisted on
 * the campaign; `name` is optional (the server derives a short label from the brief).
 */
export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { brief?: string; name?: string; vertical?: string; source?: DiscoverySource }) =>
      httpClient<CreatedCampaign>("/api/campaigns", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}
